import type { ContainerPhase, ContainerView } from '../onboarding/api.ts';
import { authorizedDomainScope } from '../authorization/scope.ts';

// Each chip's prompt is what the coordinator reads: the short label alone left it guessing.
export const WORKSPACE_QUESTIONS = [
  {
    command: '/status',
    text: 'What is it doing?',
    prompt: 'What is Traceira doing in this workspace now, and what did the latest scan do?',
  },
  {
    command: '/why',
    text: 'Why?',
    prompt: 'Why is it doing this, and what is the current or latest step meant to establish?',
  },
  {
    command: '/scope',
    text: 'What may it touch?',
    prompt:
      'What may it touch: which targets does the signed authorization allow, what is excluded, at what testing level and in what window?',
  },
  {
    command: '/approval',
    text: 'Who approved it?',
    prompt:
      'Who approved this work: who signed the authorization letter, in what role and when, and who started or stopped the latest scan?',
  },
  {
    command: '/evidence',
    text: 'Where’s the proof?',
    prompt:
      'Where is the proof: what kinds of evidence are retained, and which records back the most severe findings?',
  },
  {
    command: '/changes',
    text: 'What changed?',
    prompt:
      'What changed recently: the latest scan result, and new or updated findings, reviews, remediation or retests?',
  },
] as const;

const ALIASES: Record<string, string> = {
  '/now': '/status',
  '/approvals': '/approval',
  '/changed': '/changes',
  '/proof': '/evidence',
};

/** The plain question a command stands for, so the model reads "What may it touch?", not "/scope". */
export function questionText(input: string): string {
  const typed = input.trim().toLowerCase().replaceAll('’', "'");
  const alias = ALIASES[typed] ?? typed;
  const question = WORKSPACE_QUESTIONS.find(
    (q) => q.command === alias || q.text.toLowerCase().replaceAll('’', "'") === typed,
  );
  return question?.prompt ?? input.trim();
}

/**
 * The answer text of a reply. Answers saved before the coordinator unpacked the model's JSON
 * (2026-10-24) can hold the raw object, sometimes cut off; show its answer, never the braces.
 */
export function readableAnswer(text: string): string {
  const trimmed = text.trim();
  if (!trimmed.startsWith('{')) return text;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (parsed && typeof (parsed as { answer?: unknown }).answer === 'string') {
      return (parsed as { answer: string }).answer;
    }
  } catch {
    // Cut off or loosely quoted: read the answer string up to its closing quote or the end.
  }
  const value = /"answer"\s*:\s*"((?:[^"\\]|\\.)*)/.exec(trimmed)?.[1];
  if (!value) return text;
  try {
    return JSON.parse(`"${value.replace(/\\$/, '')}"`) as string;
  } catch {
    return value;
  }
}

/** How a remembered question reads in the log: the chip label for a chip's prompt. */
export function questionLabel(asked: string): string {
  return WORKSPACE_QUESTIONS.find((q) => q.prompt === asked)?.text ?? asked;
}

export interface WorkspaceContext {
  domain: string;
  signedScopeDomains?: string[];
  phase: ContainerPhase;
  container?: ContainerView | null;
  authorizerName?: string;
  authorizationConfirmedAt?: string;
}

export function workspaceAnswer(input: string, context: WorkspaceContext): string {
  const normalized = input
    .trim()
    .toLowerCase()
    .replaceAll('’', "'")
    .replace(/[?.]+$/, '');
  const rawCmd = ALIASES[normalized] ?? normalized;
  const question = WORKSPACE_QUESTIONS.find(
    (q) =>
      q.command === rawCmd ||
      q.text
        .toLowerCase()
        .replaceAll('’', "'")
        .replace(/[?.]+$/, '') === normalized,
  );
  const { container, phase, domain, signedScopeDomains } = context;
  const stale = container?.stale ? ' This is the last known status; live updates are delayed.' : '';
  switch (question?.command ?? rawCmd) {
    case '/help':
      return WORKSPACE_QUESTIONS.map((q) => `${q.command} — ${q.text}`).join('\n');
    case '/status':
      return `Workspace container: ${phase}.${container?.notice ? ` ${container.notice.message}` : ''} This view reports container setup and workspace output. No analysis run is reported here.${stale}`;
    case '/why':
      return 'The workspace container provides an isolated environment for authorized work. Creating it does not start a security analysis.';
    case '/scope':
      return signedScopeDomains?.length
        ? `The signed scope includes ${authorizedDomainScope(signedScopeDomains)}. Open View authorization for exclusions, testing level, and time window. A running container does not expand that scope.`
        : `The verified workspace domain is ${domain}. Open View authorization for the recorded targets, exclusions, and testing window. A running container does not expand that scope.`;
    case '/approval':
      return context.authorizationConfirmedAt
        ? `Onboarding authorization confirmed${context.authorizerName ? ` by ${context.authorizerName}` : ''} at ${context.authorizationConfirmedAt}. Open View authorization for its current status and full terms.`
        : 'Open View authorization to see the recorded authorizer, confirmation, and current status.';
    case '/evidence':
      return `The setup rows come from the workspace service${container?.observedAt ? `, observed at ${container.observedAt}` : ''}. They show container provisioning, not security findings. Open View authorization for the signed record.${stale}`;
    case '/changes': {
      const latest = container?.stages.filter((s) => s.at && s.status === 'done').at(-1);
      return latest
        ? `${latest.label} at ${latest.at}. Container attempt: ${container?.attempt ?? 'not reported'}.${stale}`
        : `No completed setup stages have been reported yet.${stale}`;
    }
    default:
      return 'I can explain the recorded workspace setup and status. Try a suggested question or /help. Free-form AI answers are not available yet. The terminal is read-only; use the runtime agent composer when messaging is connected.';
  }
}
